import { NavigateFunction } from 'react-router-dom'
import {
  auditMultiChannelSubmissionsApi,
  openWithTenant,
  associateExistingCaseData,
} from '@app-store'
import { FaxDetailsData } from '../../../types/intakeQueue.types'
import { DUPLICATE_POPUP, ENTITY_NAME } from '../../../utils/constants'
import { closeDuplicateWorkflowCase } from './closeDuplicateWorkflowCase'
import { createIntakeAttachTask } from './createIntakeAttachTask'
import { saveCaseSummaryOutcome } from './saveCaseSummaryOutcome'
import { SaveCaseSummaryOutcomeParams } from './saveCaseSummaryOutcome'

export interface DedupeOutcomeSaveFlowParams {
  attachedCaseId: string | number
  outcomeId: string
  outcomeReasonId: string
  outcomeReasonLabel: string
  attachCase: (caseId: string | number, reason: string) => Promise<void>
  updateStatusToCompleted?: () => void | Promise<void>
  faxDetails?: FaxDetailsData | null
  communicationId?: string | number
  caseDocumentTypeId?: number
  assigneeUserId?: number
  workflowInstanceId?: string
  caseId?: string | number
  workflowDefinition: SaveCaseSummaryOutcomeParams['workflowDefinition']
  onTaskCreationError?: (error: unknown) => void
  navigate?: NavigateFunction
  queueParams?: {
    entityid?: string
    layoutid?: string
    id?: string
  }
  requestorId?: number | null
  tenantId?: number | string | null
}

export const executeDedupeOutcomeSaveFlow = async ({
  attachedCaseId,
  outcomeId,
  outcomeReasonId,
  outcomeReasonLabel,
  attachCase,
  updateStatusToCompleted,
  faxDetails,
  communicationId,
  caseDocumentTypeId,
  assigneeUserId,
  workflowInstanceId,
  caseId,
  workflowDefinition,
  onTaskCreationError,
  navigate,
  queueParams,
  requestorId,
  tenantId,
}: DedupeOutcomeSaveFlowParams) => {
  await saveCaseSummaryOutcome({
    outcomeId,
    outcomeReasonId,
    caseId,
    workflowDefinition,
  })

  try {
    await createIntakeAttachTask({
      attachedCaseId,
      faxDetails,
      assigneeUserId,
    })

    const resolvedCommunicationId = Number(communicationId ?? faxDetails?.id)

    if (caseDocumentTypeId && resolvedCommunicationId && caseId) {
      await auditMultiChannelSubmissionsApi({
        existing_case_id: Number(attachedCaseId),
        new_case_id: Number(caseId),
        communication_id: resolvedCommunicationId,
        case_document_type_id: Number(caseDocumentTypeId),
      })

      await associateExistingCaseData({
        existingCaseId: Number(attachedCaseId),
        newCaseId: Number(caseId),
        requestorId: requestorId ?? null,
        tenantId: tenantId ?? '',
      })
    }
  } catch (error) {
    console.error(error)
    onTaskCreationError?.(error)
  }

  await attachCase(attachedCaseId, outcomeReasonLabel)
  await updateStatusToCompleted?.()

  await closeDuplicateWorkflowCase({
    workflowInstanceId,
    caseId,
    workflowDefinition,
  })

  openWithTenant(
    `/entity/${ENTITY_NAME.CASE_ENTITY_ID}/workflow/review/${attachedCaseId}?tabs=${DUPLICATE_POPUP.WORKFLOW_REVIEW_CORRESPONDENCE_TAB}`,
    '_blank'
  )

  const queueUrl =
    queueParams?.entityid && queueParams?.layoutid && queueParams?.id
      ? `/manage-queue/dynamic-queue/${queueParams.entityid}/${queueParams.layoutid}/${queueParams.id}`
      : '/manage-queue'

  if (navigate) {
    navigate(queueUrl)
  } else {
    openWithTenant(queueUrl, '_self')
  }
}
